/**
 * Hook for updating a single CRM object property with optimistic updates.
 *
 * The target object is supplied **per mutate call**, not at hook-call time.
 * This makes one hook instance safe to share across many objects (e.g. every
 * row of a table): a pending save always closes over the object it was given,
 * so an edit committed via delayed blur can never save against whichever
 * object the UI moved to next.
 *
 * Optimism is written to the CRM object store only (via
 * `applyOptimisticPropertyUpdate`) — read by store-backed surfaces such as
 * table cells (`useCellSubscription`). The detail query cache never holds a
 * full object body (#10010 Key Decision #2): `crmObjectDetailQuery`'s queryFn
 * upserts fetched objects into the store and returns a status-only `null`
 * sentinel, so this hook reads fresh server state back out of the store
 * (via `fetchCrmObjectDetail`) rather than off the query cache.
 *
 * The store write happens synchronously at the top of `mutationFn` rather
 * than in `onMutate`. This is deliberate: the submit seq must be captured
 * BEFORE the optimistic store write, or the seq lookup would read back our
 * own bump and ship `last_seq + 2` — which the backend rejects as a
 * non-recoverable "seq ahead" validation error. Keeping capture and write in
 * one function makes that ordering impossible to break from the outside.
 *
 * Includes smart retry logic for sequence collisions:
 * - On 409 Conflict, refetches the object to get the latest state
 * - If the same property was changed by someone else → shows conflict error
 * - If a different property changed → silently retries with fresh seq
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CrmObject } from '../../../types/crm';
import { MAX_CONFLICT_RETRIES } from '../core/constants';
import { createCRMObjectClient } from '../core/CRMObjectClient';
import { PropertyTypeConflictError } from '../core/errors';
import { createEventBuilder } from '../core/EventBuilder';
import type { EntityTypeName } from '../core/types';
import { crmQueryKeys, fetchCrmObjectDetail } from '../query/queries';
import { getObjectSeq } from '../store/crmObjectStore';
import {
  applyOptimisticPropertyUpdate,
  rollbackOptimisticBatch,
} from '../store/optimisticBatch';
import { getConflictingPropertyKeys } from './conflictDetection';

/**
 * Thrown (rather than a bare `Error`) when `.mutate()`/`.mutateAsync()` is
 * invoked before the hook's `teamId`/`userId` scope has resolved.
 *
 * The two mutation APIs surface it differently — and callers must not assume
 * `.mutate()` rejects:
 * - `.mutateAsync()` returns a promise that REJECTS with this error, so a
 *   `try/catch` around the awaited call works.
 * - `.mutate()` does NOT return a rejecting promise; it reports the error
 *   through React Query's mutation error state and the `onError` callback.
 *   A `try/catch` around `.mutate()` will never see it.
 *
 * Either way the typed class lets a caller distinguish "not ready yet" from a
 * real submit failure via `instanceof MissingCrmScopeError`.
 */
export class MissingCrmScopeError extends Error {
  constructor() {
    super(
      'useUpdateCrmProperty: mutate() called before teamId/userId resolved'
    );
    this.name = 'MissingCrmScopeError';
    Object.setPrototypeOf(this, MissingCrmScopeError.prototype);
  }
}

export interface UpdateCrmPropertyVariables {
  /**
   * The object being updated, as the user currently sees it. Captured per
   * mutate call so concurrent edits on different rows can't cross wires.
   * Its property value is the conflict-detection baseline.
   */
  object: CrmObject;
  propertyName: string;
  value: unknown;
}

/**
 * Hook to update a single property on a CRM object.
 *
 * `teamId`/`userId` are optional because at least one call site
 * (`crm-object-list-page.tsx`) constructs this hook with `currentOrg?.id` /
 * `user?.id`, which can genuinely be `undefined` while org/user context is
 * still loading. The hook itself never blocks on that — it's the caller's
 * `.mutate()`/`.mutateAsync()` invocation that will reject with
 * `MissingCrmScopeError` if it's ever called during that window. Callers
 * that always have a resolved teamId/userId by the time they mutate (e.g.
 * `ManageCannedResponsesModal`) never hit this path.
 *
 * @param teamId - Team ID
 * @param userId - ID of the authenticated user performing the action
 * @returns React Query mutation; pass the target object per mutate call.
 *
 * @example
 * const updateProperty = useUpdateCrmProperty(teamId, userId);
 *
 * updateProperty.mutate({
 *   object: integration,
 *   propertyName: 'ahoy_default_data_visibility',
 *   value: 'org',
 * });
 */
export function useUpdateCrmProperty(
  teamId: number | undefined,
  userId: number | undefined
) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, UpdateCrmPropertyVariables>({
    mutationFn: async ({ object, propertyName, value }) => {
      if (!teamId || userId == null) {
        throw new MissingCrmScopeError();
      }

      // Capture the base value the user saw before editing. `object` is the
      // mutate-time argument (not the cache), so it represents the state
      // before the optimistic updates below.
      const baseValue = object.properties?.[propertyName]?.value;

      const entityType = object.entity_type_name as EntityTypeName;
      const queryKey = crmQueryKeys.objectDetail(
        teamId,
        entityType,
        object.entity_id
      );

      // Cancel outgoing refetches so an in-flight response can't land on top
      // of the optimistic write we're about to make.
      await queryClient.cancelQueries({ queryKey });

      // The freshest known COMMITTED seq, captured before we write anything:
      // the store is kept live by the WebSocket, and the caller-supplied
      // object may be a stale list-row snapshot — take the max so a stale
      // source can't manufacture avoidable sequence conflicts. The detail
      // query cache is not consulted (#10010 Key Decision #2):
      // `crmObjectDetailQuery`'s queryFn upserts into the store and returns a
      // null sentinel, so the cache never holds a real seq at this key.
      // After the optimistic store write below, the store seq includes our
      // own bump and must not be read again.
      const initialStartSeq = Math.max(
        getObjectSeq(object.entity_id) ?? 0,
        object.last_event_seq
      );

      // Optimistically write to the CRM object store for store-backed readers
      // (table cells). The seq mirrors what the first submit attempt will
      // carry (startSeq + 1) so the WS echo of a success is deduped.
      //
      // NOTE: Do NOT bump last_event_seq anywhere else. The retry loop below
      // tracks the last known committed seq in a local variable, and
      // EventBuilder itself emits startSeq + 1. Bumping the seq a second time
      // would double-increment it (the event ships with last_seq + 2), which
      // the backend treats as a non-recoverable "seq ahead of entity's
      // last_seq" validation error (a 400, not a retryable 409 collision).
      const storeContext = applyOptimisticPropertyUpdate({
        entityId: object.entity_id,
        entityTypeName: object.entity_type_name,
        propertyName,
        value,
        seq: initialStartSeq + 1,
        sourceId: String(userId),
        teamId,
      });

      // Roll back the optimistic store write (seq-guarded: newer
      // WS-delivered state that arrived mid-flight is preserved). There is no
      // query-cache write to restore — optimism lives only in the store.
      const rollbackOptimism = () => {
        rollbackOptimisticBatch(storeContext);
      };

      // Carries the server-fresh object (with our pending property re-overlaid)
      // forward across retry attempts. Populated by the conflict branch below
      // after a refetch; a local value only (#10010 Key Decision #2 — never
      // written back into the query cache).
      let latestKnownObject: CrmObject | null = null;

      try {
        for (let attempt = 0; attempt <= MAX_CONFLICT_RETRIES; attempt++) {
          try {
            // First attempt uses the seq captured above. Retries use the fresh
            // seq the conflict branch below fetched from the server. The
            // store is NOT consulted again — it now contains our own
            // optimistic bump.
            const startSeq =
              attempt === 0
                ? initialStartSeq
                : Math.max(
                    latestKnownObject?.last_event_seq ?? 0,
                    object.last_event_seq
                  );

            const client = createCRMObjectClient(teamId, entityType);
            const builder = createEventBuilder({
              sourceType: 'ahoy_user',
              sourceId: String(userId),
              startSeq,
            });

            builder.propertyUpdated(
              object.entity_id,
              object.entity_type_name,
              propertyName,
              value
            );

            const events = builder.build();
            return await client.submitEvents({ events });
          } catch (error) {
            if (error instanceof PropertyTypeConflictError) {
              // Refetch the object to get current server state.
              // crmObjectDetailQuery's queryFn upserts into crmObjectStore
              // and returns a null sentinel — fetchCrmObjectDetail reads the
              // refreshed body back out of the store afterward.
              const freshObject = await fetchCrmObjectDetail(
                queryClient,
                teamId,
                entityType,
                object.entity_id
              );

              // Check if the property WE are editing was changed by someone
              // else
              const hasConflict =
                getConflictingPropertyKeys(
                  [propertyName],
                  freshObject.properties ?? {},
                  { [propertyName]: baseValue }
                ).length > 0;
              if (hasConflict) {
                // Real conflict: same property was modified by another user
                const who = error.lastChangedBy?.name;
                const msg = who
                  ? `${who} just made a change to this field. Please check the latest value and try again.`
                  : 'Someone else just made a change to this field. Please check the latest value and try again.';
                throw new Error(msg);
              }

              if (attempt < MAX_CONFLICT_RETRIES) {
                // Different property changed — carry the fresh object forward
                // (preserving our optimistic property value) for the next
                // retry attempt and retry.
                latestKnownObject = {
                  ...freshObject,
                  properties: {
                    ...freshObject.properties,
                    [propertyName]: { value },
                  },
                };
                continue;
              }
              throw error;
            }
            throw error;
          }
        }

        // Should not reach here, but satisfy TypeScript
        throw new Error('Max retries exceeded for sequence conflict');
      } catch (error) {
        rollbackOptimism();
        throw error;
      }
    },

    onSettled: async (_data, _error, variables) => {
      if (!teamId) {
        return;
      }

      const entityType = variables.object.entity_type_name as EntityTypeName;

      // Invalidate queries to mark them as stale (both active and inactive)
      // Active queries refetch immediately, inactive ones refetch when
      // remounted
      await queryClient.invalidateQueries({
        queryKey: crmQueryKeys.objectDetail(
          teamId,
          entityType,
          variables.object.entity_id
        ),
      });
      // Mark this entity type's object queries (lists + details) stale so
      // they revalidate lazily on next mount/focus, without refetching what
      // the store already holds via the optimistic + WS-confirmed value.
      await queryClient.invalidateQueries({
        queryKey: crmQueryKeys.objects(teamId, entityType),
        refetchType: 'none',
      });
    },
  });
}
